import { Button, ClickEvent, DataHeader, DataSourceMode, DataSourceModeChangeEvent, DataSourceValidationEvent, DropdownItem, SlideoutDecorator, TableRow } from "@mcleod/components";
import { AutogenLayoutTopMatchProfile } from "./autogen/AutogenLayoutTopMatchProfile";
import { TopMatchProfileDetail } from "./TopMatchProfileDetail";
import { ModelRow, StringUtil } from "@mcleod/core";
import { CommonDialogs } from "@mcleod/common";
import { DuplicateId, DuplicateIdProps } from "@mcleod/dispatch";
import { LinkedModel } from "@mcleod/core/src/ModelRow";

const viewProfileUrl = "lme/powerbroker/TopMatchProfile?mode=update&key=";
const DUPLICATE_VALIDATION_PROPS: DuplicateIdProps = {
    tableName: "integrated_search_profile",
    screenName: "TopMatch Profile",
    navigateToUrl: viewProfileUrl
}

export class TopMatchProfile extends AutogenLayoutTopMatchProfile {
    
    onLoad(): void | Promise<void> {
        this.mainDataSource.addValidationListener((event: DataSourceValidationEvent) => {
            if (event.dataSource.mode == DataSourceMode.SEARCH) {
                return;
            }
            this.textboxWeight.validationWarning = null;
            if (this.tableProfileDetails.rowCount < 1){
                event.validationResults.push({ 
                    isValid: false, 
                    component: this.textboxWeight,
                    caption:'No TopMatch Details'
                });
                this.textboxWeight.validationWarning = `Please provide at least one TopMatch Detail.`;
            }
        });

        this.tableProfileDetails.onEditLayoutLoaded = (decorator, tableRow) => {
            const topMatchProfileDetail = decorator.layout as TopMatchProfileDetail;
            topMatchProfileDetail.addLayoutDataLoadListener(event => {
                const searchType = tableRow.data?.getString('search_type');
                topMatchProfileDetail.syncSearchType(searchType);
                const existingSearchTypes = this.getExistingSearchTypes().filter(type => !StringUtil.equalsIgnoreCase(searchType, type));
                const items = topMatchProfileDetail.textboxSearchType.items as DropdownItem[];
                topMatchProfileDetail.textboxSearchType.items = items.filter(item => !existingSearchTypes.includes(item.value));
                if (!tableRow.data.isNull('detail_groups')) {
                        const detailGroups = tableRow.data.get('detail_groups');
                        topMatchProfileDetail.sourceTopmatchDetailGroup.setRowsAndMode(DataSourceMode.UPDATE, detailGroups);
                }
            });
            
            decorator.multiButton.dropdownItems = null;
            const isSearch = this.mainDataSource.mode === DataSourceMode.SEARCH;
            if (isSearch) {
                decorator.labelTitle.text = 'Search TopMatch Profile Details'
            }
            decorator.multiButton.caption =  (isSearch)
                ? 'Apply Filters'
                : 'Apply Edits';
            decorator.doAfterSlideOut = (_) => {
                tableRow.displayComponentValues();
            }
            const superOnSave = decorator.onSave;
            decorator.onSave = (updatedRow) => {
                if (decorator.layout.mainDataSource.validate()) { 
                    const detailGroups = topMatchProfileDetail.tableDetailGroup?.data;
                    updatedRow.set('detail_groups', detailGroups);
                    superOnSave(updatedRow);
                }
            };
        }

        this.mainDataSource.doBeforeDataSourcePost = async () => {
            let continuePost = true;
            
            if (DataSourceMode.ADD === this.mainDataSource.mode) {
                continuePost = await DuplicateId.isValidCode(this.textboxProfileName, DUPLICATE_VALIDATION_PROPS);
            }

            if (continuePost
            && DataSourceMode.SEARCH !== this.mainDataSource.mode
            && !StringUtil.equalsIgnoreCase('100%', this.textboxWeight.getBasicValue())) {
                continuePost = await CommonDialogs.showYesNo(
                    "The total of the weights does not equal 100%. We recommend a total of 100% for each profile.\n\nWould you like to continue saving?",
                    "Weighting Incomplete"
                );
                if (continuePost == undefined) {
                    return false;
                }
            }
            return continuePost;
        }
        DuplicateId.setupValidateOnBlur(this.textboxProfileName, DUPLICATE_VALIDATION_PROPS);
    }

    public override doAfterDataHeaderSetup(dataHeader: DataHeader): void {
        dataHeader.showDelete = true;
    }

    sourceTopmatchProfileAfterModeChange(event: DataSourceModeChangeEvent) {
        this.buttonAddDetail.visible = event.newMode !== DataSourceMode.SEARCH;
        this.buttonSearchDetails.visible = event.newMode === DataSourceMode.SEARCH;
        if (event.oldMode === DataSourceMode.ADD && event.newMode === DataSourceMode.UPDATE) {
            const childLinks: LinkedModel[] = this.sourceTopmatchProfile.activeRow?.data["_child_links"];
            childLinks?.forEach(linkedModel => linkedModel.rows?.forEach(row => row._appending = false));
        }
    }

    tableRowOnDblClick(event: ClickEvent) {
        const tableRow = event.target as TableRow;
        tableRow.showEditLayout();
    }

    tableProfileDetailsOnContentsChanged(event) {
        this.syncWeightTotal();
    }

    sourceTopmatchProfileDetailsAfterExecution(event) {
        this.syncWeightTotal();
    }

    syncWeightTotal() {
        const weightTotal = this.tableProfileDetails.allRows
                            .map((row) => row.data.get('weight', 0))
                            .reduce((prev, curr) => {return prev + curr}, 0);
        this.textboxWeight.text = `${weightTotal}%`;
    }

    buttonAddOnClick(event: ClickEvent) {
        const saveButton = this.createSaveButton();
        SlideoutDecorator.decorateAndSlideIn('lme/powerbroker/TopMatchProfileDetail', {
            fillVerticalSpace: true,
            width: '50%',
            title: 'Add TopMatch Profile Detail',
            overlayProps: { closeOnClickOff: false, greyedBackground: true },
            addlComponents: saveButton,
            layoutLoadListeners: async (event) => {
                const layout = event.target as TopMatchProfileDetail;
                layout.mainDataSource.setRowsAndMode(DataSourceMode.ADD, [await layout.mainDataSource.createBlankRow()]);
                layout.sourceTopmatchDetailGroup.setRowsAndMode(DataSourceMode.UPDATE, []);
                const existingSearchTypes = this.getExistingSearchTypes();
                const items = layout.textboxSearchType.items as DropdownItem[];
                layout.textboxSearchType.items = items.filter(item => !existingSearchTypes.includes(item.value));
            },
            doAfterSlideIn: async (decorator: SlideoutDecorator) => {
                saveButton.addClickListener(event => {
                    saveButton.busy = true;
                    if (decorator.layout.mainDataSource.validate()) {
                        const topMatchProfileDetail = decorator.layout as TopMatchProfileDetail;
                        const topMatchProfileDetailRow = topMatchProfileDetail.activeRow;
                        const detailGroups = topMatchProfileDetail.tableDetailGroup?.data;
                        topMatchProfileDetailRow.set('detail_groups', detailGroups);
                        decorator.slideOut();
                        this.tableProfileDetails.addRow(topMatchProfileDetailRow, null, { display: true, addToData: true });
                    }
                    saveButton.busy = false;
                });
            }
        });
    }

    buttonSearchDetailsOnClick(event: ClickEvent) {
        const tableRow = this.tableProfileDetails.allRows[0]
        tableRow.showEditLayout();
    }

    createSaveButton() {
        return new Button({
            id: "buttonAddTopmatchProfile",
            caption: "Add Detail",
            backgroundColor: "primary",
            minWidth: 128,
            borderWidth: 0,
            rowBreak: false,
            default: true
        });
    }

    applyCloneValues(result: any) {
        const { details: details, ...profileValues } = result;
        const profileRow = new ModelRow(this.sourceTopmatchProfile.url, true, profileValues);
        const detailRows = details.map(detail => {
            const { detail_groups: detailGroups, ...detailValues} = detail;
            const detailRow = new ModelRow(this.sourceTopmatchProfileDetails.url, true, detailValues);
            const detailGroupRows = detailGroups.map(detailGroup => 
                new ModelRow('lme/powerbroker/topmatch-detail-group', true, detailGroup)
            )
            detailRow.set('detail_groups', detailGroupRows);
            return detailRow;
        });
        this.sourceTopmatchProfile.setRowsAndMode(DataSourceMode.ADD, [profileRow]);
        this.sourceTopmatchProfileDetails.setRowsAndMode(DataSourceMode.ADD, detailRows);
        this.syncWeightTotal();
    }

    getExistingSearchTypes(): string[] {
        return this.tableProfileDetails.allRows
            .map(tableRow => tableRow.data.get('search_type', null));
    }
}
